import { useTeam } from '../context/TeamContext';
import { useAuth } from '../context/AuthContext';
import './TeamSelector.css';

/**
 * TeamSelector Component (Task 11.2)
 *
 * Rendered in the Header. Allows users to switch between their authorised
 * teams. Admins see an "All teams" option. Single-team users see a static
 * label (no dropdown). Persists selection to localStorage which triggers
 * the X-Active-Team-Id header on subsequent API calls.
 *
 * Requirements: R17.5
 */
export default function TeamSelector() {
  const { authorisedTeams, activeTeamId, setActiveTeamId, loading, error } = useTeam();
  const { user } = useAuth();

  // Don't render if loading, errored, or no teams
  if (loading || error || authorisedTeams.length === 0) return null;

  // Single team (non-admin): show static label, no dropdown
  if (authorisedTeams.length === 1 && user?.role !== 'admin') {
    return (
      <div className="team-selector team-selector--static">
        <span className="team-selector__icon">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
        </span>
        <span className="team-selector__label">{authorisedTeams[0].name}</span>
      </div>
    );
  }

  const handleChange = (e) => {
    const value = e.target.value;
    setActiveTeamId(value === '' ? null : value);
    // Trigger page refresh so data reloads for the new team
    window.location.reload();
  };

  return (
    <div className="team-selector">
      <span className="team-selector__icon">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      </span>
      <select
        className="team-selector__select"
        value={activeTeamId ?? ''}
        onChange={handleChange}
        aria-label="Select active team"
      >
        {authorisedTeams.map((team) => (
          <option key={team.id ?? 'all'} value={team.id ?? ''}>
            {team.name}
          </option>
        ))}
      </select>
    </div>
  );
}
